import { Component, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DomainType, GetPendingEmailDomainResponseInterface } from '@vendasta/email';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { DomainCacheService } from '../domain-cache.service';
import { SenderID } from '../shared-types';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { EmailUII18nModule } from '../../../src/lib/i18n/email-ui-i18n.module';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

export interface NewDomainDialogData {
  senderID: SenderID;
  enableDomainTypeSelection?: boolean;
}

export interface Result {
  newPendingDomain?: string;
}

@Component({
  selector: 'email-new-domain-dialog',
  templateUrl: './new-domain-dialog.component.html',
  styleUrls: ['./new-domain-dialog.component.scss'],
  providers: [DomainCacheService],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    TranslateModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyPipesModule,
    EmailUII18nModule,
    TranslateModule,
    MatDialogModule,
    GalaxyFormFieldModule,
    GalaxyAlertModule,
  ],
})
export class NewDomainDialogComponent {
  readonly senderForm = new UntypedFormGroup({
    senderDomain: new UntypedFormControl('', [Validators.required, Validators.pattern('[^(@\\s)]+\\.[^(@\\s)]+')]),
    shareWithOtherBusiness: new UntypedFormControl(false),
  });

  get enableDomainTypeSelection(): boolean {
    return this.data.enableDomainTypeSelection || false;
  }
  authorizedDomain$: Observable<GetPendingEmailDomainResponseInterface> = of(null);
  awaitingTypeChangeConfirmation = false;
  typeChangeBlocked = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: NewDomainDialogData,
    @Inject(MatDialogRef) private readonly dialog: MatDialogRef<NewDomainDialogComponent>,
    private readonly domainService: DomainCacheService,
    private readonly alerts: SnackbarService,
  ) {
    // Editing the domain or toggling the share option invalidates a pending confirmation.
    this.senderForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.awaitingTypeChangeConfirmation = false;
      this.typeChangeBlocked = false;
    });
  }

  private authorize(confirmedTypeChange: boolean): void {
    const domain = this.senderForm.get('senderDomain').value;

    const options: { domainType?: DomainType; confirmedTypeChange?: boolean } = {};

    // Only handle domain type if the feature is enabled for this app. Only pass the type when
    // shared is checked, otherwise let the backend use its default (dedicated).
    if (this.enableDomainTypeSelection && this.senderForm.get('shareWithOtherBusiness').value) {
      options.domainType = DomainType.DOMAIN_TYPE_SHARED;
    }
    if (confirmedTypeChange) {
      options.confirmedTypeChange = true;
    }

    this.authorizedDomain$ = this.domainService
      .createPendingDomain(this.data.senderID.senderType, this.data.senderID.senderID, domain, options)
      .pipe(
        catchError(() => {
          this.alerts.openErrorSnack('DOMAIN_AUTHORIZATION.NEW_DOMAIN_DIALOG.AUTH_ERROR');
          return of(null);
        }),
        tap((response) => {
          if (response?.typeChangeConfirmationRequired) {
            // dedicated -> shared is allowed with confirmation; the reverse (shared -> dedicated)
            // is not supported, so show a blocking error instead of offering to continue.
            const requestingShared = this.enableDomainTypeSelection && this.senderForm.get('shareWithOtherBusiness').value;
            if (requestingShared) {
              this.awaitingTypeChangeConfirmation = true;
            } else {
              this.typeChangeBlocked = true;
            }
            return;
          }
          if (response) {
            this.dialog.close({ newPendingDomain: domain } as Result);
          }
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
  }

  close(): void {
    this.dialog.close({ newPendingDomain: undefined } as Result);
  }

  goBack(): void {
    this.awaitingTypeChangeConfirmation = false;
    this.typeChangeBlocked = false;
  }

  submitForm(): void {
    this.senderForm.markAsDirty();
    if (this.senderForm.valid) {
      this.authorize(this.awaitingTypeChangeConfirmation);
    } else {
      this.alerts.openErrorSnack('DOMAIN_AUTHORIZATION.NEW_DOMAIN_DIALOG.INVALID_ERROR');
    }
  }
}
